import { HighlightIqLimitType } from '@shared/enumsCustom/HighlightIqLimitType';
import { HighlightIqTeamSide } from '@shared/enumsCustom/HighlightIqTeamSide';
import { ParticipantType } from '@shared/enums/ParticipantType';
import { TagName } from '@shared/enums/TagName';
import { buildHighlightIqMetadataPeriods, mergeFormulaConfig } from './HighlightIqService';

const SCORE_TAG_NAMES = new Set([TagName.ACTION_GOAL, TagName.ACTION_7M_GOAL, TagName.ACTION_POINT]);

function resolveTeamSide(tag, event) {
	const homeId = event?.teamHome?.id ??
		event?.participants?.find(participant => participant.type === ParticipantType.HOME)?.participatable?.id ??
		null;
	const guestId = event?.teamGuest?.id ??
		event?.participants?.find(participant => participant.type === ParticipantType.GUEST)?.participatable?.id ??
		null;
	if (tag.teamId === homeId) {
		return HighlightIqTeamSide.HOME;
	}
	if (tag.teamId === guestId) {
		return HighlightIqTeamSide.AWAY;
	}
	return null;
}

function getFinalScores(event) {
	const teamHomeScore = Number(event?.finalScore?.teamHomeScore ?? 0);
	const teamGuestScore = Number(event?.finalScore?.teamGuestScore ?? 0);
	return {
		homeScore: Number.isFinite(teamHomeScore) ? teamHomeScore : 0,
		awayScore: Number.isFinite(teamGuestScore) ? teamGuestScore : 0,
	};
}

/**
 * @param {object} params
 * @param {Array} params.eventTagsWithoutClockTags
 * @param {object} params.event
 * @param {string} params.sportName
 * @param {string|object} params.formulaType
 * @param {string} params.limitType - HighlightIqLimitType value (clips or duration)
 * @param {number} params.limitValue
 * @param {object|null} params.effectiveFormulaConfig
 * @param {object|null} params.formulaCatalog
 * @param {number} params.durationSeconds
 * @param {boolean} [params.debug]
 */
export function buildHighlightIqSelectPayload({
	eventTagsWithoutClockTags,
	event,
	sportName,
	formulaType,
	limitType = HighlightIqLimitType.CLIPS,
	limitValue,
	effectiveFormulaConfig,
	formulaCatalog = null,
	durationSeconds,
	debug = false,
}) {
	const mergedFormulaConfig = mergeFormulaConfig(formulaCatalog, effectiveFormulaConfig);
	const formulaKey = typeof formulaType === 'object' && formulaType.value ? formulaType.value : formulaType;
	const sortedTags = [...eventTagsWithoutClockTags].sort((a, b) => a.timestamp - b.timestamp);

	let homeRunning = 0;
	let awayRunning = 0;
	const tags = sortedTags.map(tag => {
		const name = tag.tag?.name;
		const teamSide = resolveTeamSide(tag, event);
		const loudnessRaw = tag.data?.loudnessRating;
		const loudness = loudnessRaw === undefined || loudnessRaw === null
			? null
			: Number(loudnessRaw);

		const row = {
			id:               tag.id,
			timestamp:        tag.timestamp,
			tagName:          name ?? '',
			teamSide,
			currentHomeScore: homeRunning,
			currentAwayScore: awayRunning,
			loudness:         Number.isFinite(loudness) ? loudness : null,
			secondsBefore:    tag.secondsBefore,
			secondsAfter:     tag.secondsAfter,
		};

		if (name && SCORE_TAG_NAMES.has(name)) {
			if (teamSide === HighlightIqTeamSide.HOME) {
				homeRunning += 1;
			} else if (teamSide === HighlightIqTeamSide.AWAY) {
				awayRunning += 1;
			}
		}

		return row;
	});

	const { homeScore, awayScore } = getFinalScores(event);
	const durationFloored = Math.floor(Number(durationSeconds) || 0);
	const periods = buildHighlightIqMetadataPeriods(
		eventTagsWithoutClockTags,
		mergedFormulaConfig,
		durationFloored
	);

	const normalizedLimitType = limitType === HighlightIqLimitType.DURATION
		? HighlightIqLimitType.DURATION
		: HighlightIqLimitType.CLIPS;
	const override = normalizedLimitType === HighlightIqLimitType.DURATION
		? { durationSeconds: limitValue }
		: { clipCount: limitValue };

	return {
		tags,
		metadata: {
			sportName,
			periods,
			durationSeconds: durationFloored,
			homeScore,
			awayScore,
		},
		formulaType: formulaKey,
		limitType:   normalizedLimitType,
		limitValue,
		override,
		debug:       Boolean(debug),
	};
}
